import React from "react";
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  MarkerType
} from "react-flow-renderer";
import dagre from "dagre";
import NodeWrapper from "./NodeWrapper";

// Define nodeTypes outside of the component
const nodeTypes = {
  custom: NodeWrapper, // Custom node for TankIcon
};

const applyDagreLayout = (nodes, edges, direction = 'TB') => {
  const nodeWidth = 180;
  const nodeHeight = 100;

  const g = new dagre.graphlib.Graph();
  const isHorizontal = direction === 'LR';

  g.setGraph({
    rankdir: direction,
    nodesep: 100,
    ranksep: 150,
  });

  g.setDefaultEdgeLabel(() => ({}));

  // 1. Group nodes by operation_id
  const operationMap = new Map();
  nodes.forEach((node) => {
    const opId = node.data?.operation_id;
    if (!opId) return;
    if (!operationMap.has(opId)) operationMap.set(opId, []);
    operationMap.get(opId).push(node);
  });

  // 2. Sort operation groups by completion_date
  const sortedOperations = Array.from(operationMap.entries()).sort(
    ([, aNodes], [, bNodes]) => {
      const aDate = new Date(aNodes[0]?.data?.completion_date || 0);
      const bDate = new Date(bNodes[0]?.data?.completion_date || 0);
      return aDate - bDate;
    }
  );

  // 3. Create dummy operation-level nodes to define order (timeline)
  const operationAnchors = [];
  sortedOperations.forEach(([opId, opNodes], index) => {
    const anchorId = `anchor_${opId}`;
    g.setNode(anchorId, { width: 1, height: 1 }); // invisible anchor node
    operationAnchors.push(anchorId);
    
    // Connect real nodes to their anchor to group them
    opNodes.forEach((node) => {
      g.setNode(node.id, { width: nodeWidth, height: nodeHeight });
      g.setEdge(anchorId, node.id); // anchor "owns" its group
    });
  });

  // 4. Add timeline ordering between operation anchors
  for (let i = 1; i < operationAnchors.length; i++) {
    g.setEdge(operationAnchors[i - 1], operationAnchors[i]);
  }

  // 5. Add real edges (if you want them to influence the layout)
  edges.forEach((edge) => {
    if (g.hasNode(edge.source) && g.hasNode(edge.target)) {
      g.setEdge(edge.source, edge.target);
    }
  });

  dagre.layout(g);

  // 6. Apply calculated positions to nodes
  const newNodes = nodes.map((node) => {
    const dagreNode = g.node(node.id);
    if (!dagreNode) return node;

    return {
      ...node,
      targetPosition: isHorizontal ? 'left' : 'top',
      sourcePosition: isHorizontal ? 'right' : 'bottom',
      position: {
        x: dagreNode.x - nodeWidth / 2,
        y: dagreNode.y - nodeHeight / 2,
      },
    };
  });

  return newNodes;
};

const getColor = (color = "RED") => {
  switch (color) {
    case "RED":
      return "#64242e";
    case "ROSE":
      return "#c74f59";
    case "WHITE":
      return "#f9d573";
    default:
      return "#64242e";
  }
};

/**
 * Normalizes an operation array from the new format into the `transfers`-based
 * shape your old FlowChart implementation expects.
 *
 * Assumptions made during normalization (see comment below):
 * - If operation.transfers exists, use it as-is.
 * - Else, create transfers by combining src_tanks and dest_tanks:
 *   - For CUT: multiple src_tanks -> one destination (first dest)
 *   - For DESTEMMING/NEW_LOT: treat src_tanks as sources (may be batch-only)
 *     and dest_tanks as destinations (one or many)
 *   - For TREATMENT/ANALYSIS: create pseudo-transfer entries from src_tanks
 *   - Fill fields: source_tank_id/source_tank_name/destination_tank_id etc.
 */
const normalizeOperations = (ops) => {
  return (ops || []).map((op) => {
    // If already in old shape (has transfers), just return a shallow-copy
    if (Array.isArray(op.transfers) && op.transfers.length > 0) {
      return { ...op, transfers: op.transfers };
    }

    const transfers = [];

    const src = op.src_tanks || [];
    const dst = op.dest_tanks || [];

    const pushTransfer = (t) => {
      // Ensure some commonly used fields exist for the rest of the flowchart logic
      transfers.push({
        // keep any provided id or create synthetic one
        transfer_id: t.transfer_id || t.id || `${op.operation_id}_${t.source_tank_id || t.tank_id || t.batch_id || Math.random().toString(36).slice(2,6)}`,
        source_tank_id: t.source_tank_id ?? t.tank_id ?? t.source_tank_id ?? null,
        source_batch_id: t.source_batch_id ?? null,
        destination_tank_id: t.destination_tank_id ?? t.tank_id ?? t.destination_tank_id ?? null,
        source_tank_name: t.source_tank_name ?? t.name ?? t.batch_name ?? null,
        destination_tank_name: t.destination_tank_name ?? t.name ?? t.batch_name ?? null,
        batch_name: t.batch_name ?? t.batch_name ?? (t.batch ? t.batch.name : undefined) ?? "",
        source_batch_name: t.source_batch_name ?? "",
        destination_batch_name: t.destination_batch_name ?? "",
        src_quantity: t.src_quantity ?? t.initial_quantity ?? t.old_quantity ?? t.quantity ?? 0,
        quantity: t.quantity ?? (t.arrow_quantity ?? 0),
        arrow_quantity: t.arrow_quantity ?? t.quantity ?? 0,
        drop: t.drop ?? 0,
        capacity: t.capacity ?? t.tank_capacity ?? null,
        color: t.color ?? (t.batch && t.batch.color) ?? null,
        batch_id: t.batch_id ?? null,
        batch_type: t.batch_type ?? null,
        tank_type: t.tank_type ?? t.type ?? null,
        // pass-through the entire original object so nothing is lost
        __raw: t
      });
    };

    // Helper: if the new format has src_tanks and dest_tanks, create transfers
    // mapping rules based on operation type
    if (op.operation_type === "CUT") {
      // multiple sources -> one destination (use first dest)
      const firstDest = dst[0] || {};
      src.forEach((s) => {
        pushTransfer({
          // source-specific fields
          source_tank_id: s.tank_id,
          source_batch_id: s.batch_id,
          source_tank_name: s.name ?? s.batch_name,
          src_quantity: s.quantity ?? s.initial_quantity ?? s.old_quantity,
          // destination is the first dest
          destination_tank_id: firstDest.tank_id,
          destination_batch_id: firstDest.batch_id,
          destination_tank_name: firstDest.name ?? firstDest.batch_name,
          batch_name: firstDest.batch_name ?? s.batch_name,
          quantity: firstDest.quantity ?? s.quantity ?? 0,
          arrow_quantity: s.arrow_quantity ?? s.quantity ?? 0,
          color: s.color ?? firstDest.color,
          capacity: s.capacity ?? firstDest.capacity
        });
      });
    } else if (["DESTEMMING", "NEW_LOT"].includes(op.operation_type)) {
      // often batch -> one or many dest tanks
      // for each destination create a transfer using the first src as origin
      const firstSrc = src[0] || {};
      if (dst.length > 0) {
        dst.forEach((d) => {
          pushTransfer({
            source_tank_id: firstSrc.tank_id ?? null,
            source_batch_id: firstSrc.batch_id ?? null,
            source_tank_name: firstSrc.batch_name ?? firstSrc.name,
            destination_tank_id: d.tank_id,
            destination_batch_id: d.batch_id,
            destination_tank_name: d.name,
            batch_name: d.batch_name ?? firstSrc.batch_name,
            quantity: d.quantity ?? d.arrow_quantity ?? 0,
            arrow_quantity: d.arrow_quantity ?? d.quantity ?? 0,
            drop: d.drop ?? 0,
            capacity: d.capacity ?? 0,
            color: d.color ?? firstSrc.color,
          });
        });
      } else {
        // no dests: just create a pseudo-transfer for the src
        src.forEach((s) => pushTransfer({ source_tank_id: null, source_batch_id: null, source_tank_name: s.batch_name ?? s.name, quantity: s.quantity ?? s.initial_quantity ?? 0, color: s.color,  }));
      }
    } else if (op.operation_type === "BOTTLING" || op.operation_type === "CLOSING" || op.operation_type === "RACKING" || op.operation_type === "DECANT") {
      // usually single or multi dests with a source tank present
      // we'll try to match pairs by index, otherwise cross-join src[0] -> each dest
      if (src.length > 0 && dst.length > 0) {
        // if equal length, pair by index
        if (src.length === dst.length) {
          for (let i = 0; i < src.length; i++) {
            const s = src[i];
            const d = dst[i];
            pushTransfer({
              source_tank_id: s.tank_id,
              source_batch_id: s.batch_id,
              source_tank_name: s.name ?? s.batch_name,
              destination_tank_id: d.tank_id,
              destination_batch_id: d.batch_id,
              destination_tank_name: d.name ?? d.batch_name,
              batch_name: d.batch_name ?? s.batch_name,
              quantity: d.quantity ?? s.quantity ?? 0,
              arrow_quantity: d.arrow_quantity ?? s.arrow_quantity ?? 0,
              capacity: d.capacity ?? s.capacity,
              drop: d.drop ?? 0,
              color: d.color ?? s.color
            });
          }
        } else {
          // otherwise map src[0] -> each dest
          const s = src[0];
          dst.forEach((d) => {
            pushTransfer({
              source_tank_id: s.tank_id,
              source_batch_id: s.batch_id,
              source_tank_name: s.name ?? s.batch_name,
              destination_tank_id: d.tank_id,
              destination_batch_id: d.batch_id,
              destination_tank_name: d.name ?? d.batch_name,
              batch_name: d.batch_name ?? s.batch_name,
              quantity: d.quantity ?? 0,
              arrow_quantity: d.arrow_quantity ?? d.quantity ?? 0,
              capacity: d.capacity ?? s.capacity,
              drop: d.drop ?? 0,
              color: d.color ?? s.color
            });
          });
        }
      } else if (dst.length > 0) {
        dst.forEach((d) => pushTransfer({
          destination_tank_id: d.tank_id,
          destination_batch_id: d.batch_id,
          destination_tank_name: d.name ?? d.batch_name,
          batch_name: d.batch_name,
          quantity: d.quantity ?? d.arrow_quantity ?? 0,
          arrow_quantity: d.arrow_quantity ?? d.quantity ?? 0,
          capacity: d.capacity ?? 0,
          color: d.color
        }));
      } else if (src.length > 0) {
        src.forEach((s) => pushTransfer({
          source_tank_id: s.tank_id,
          source_batch_id: s.batch_id,
          source_tank_name: s.name ?? s.batch_name,
          src_quantity: s.quantity ?? s.initial_quantity ?? 0,
          arrow_quantity: s.arrow_quantity ?? s.quantity ?? 0,
          capacity: s.capacity,
          color: s.color
        }));
      }
    } else if (["TREATMENT", "ANALYSIS", "STABILIZATION_RESIN"].includes(op.operation_type)) {
      // treatment/analysis usually reference tanks in src_tanks or in treatments[].tank_id
      if (op.treatments && op.treatments.length > 0) {
        op.treatments.forEach((tr) => {
          pushTransfer({
            source_tank_id: tr.tank_id ?? (src[0] && src[0].tank_id) ?? null,
            source_batch_id: tr.batch_id ?? (src[0] && src[0].batch_id) ?? null,
            source_tank_name: null,
            destination_tank_id: null,
            destination_tank_name: null,
            quantity: tr.quantity ?? 0,
            color: tr.color ?? getColor(tr.source_batch_color)
          });
        });
      } else if (src.length > 0) {
        src.forEach((s) => pushTransfer({
          source_tank_id: s.tank_id,
          source_batch_id: s.batch_id,
          source_tank_name: s.name ?? s.batch_name,
          src_quantity: s.quantity ?? s.old_quantity ?? 0,
          color: s.color
        }));
      }
    } else {
      // Generic fallback: create one transfer per dest, using first src if available
      if (dst.length > 0) {
        const s0 = src[0] || {};
        dst.forEach((d) => {
          pushTransfer({
            source_tank_id: s0.tank_id ?? null,
            source_batch_id: s0.batch_id ?? null,
            source_tank_name: s0.name ?? s0.batch_name,
            destination_tank_id: d.tank_id,
            destination_batch_id: d.batch_id,
            destination_tank_name: d.name ?? d.batch_name,
            batch_name: d.batch_name ?? s0.batch_name,
            quantity: d.quantity ?? d.arrow_quantity ?? 0,
            arrow_quantity: d.arrow_quantity ?? d.quantity ?? 0,
            capacity: d.capacity ?? 0,
            color: d.color ?? s0.color
          });
        });
      } else if (src.length > 0) {
        src.forEach((s) => pushTransfer({
          source_tank_id: s.tank_id,
          source_batch_id: s.batch_id,
          source_tank_name: s.name ?? s.batch_name,
          src_quantity: s.quantity ?? s.old_quantity ?? 0,
          color: s.color
        }));
      }
    }

    return {
      ...op,
      transfers
    };
  });
};

const FlowChart = ({ operations, toggleShowOperationData, flowType, id = null, batchParentIds = [] }) => {

  const getMatchingTransfer = (data, currentDate, currentSrcTankId, currentDestTankId = null) => {
    // return always the last match
    let latestMatch = null;
    let latestDate = null;
    
    for (const prevOp of data) {
      if (!prevOp.completion_date || !prevOp.transfers) continue;
      
      const prevDate = new Date(prevOp.completion_date);

      if (prevDate < currentDate) {
        const match = prevOp?.transfers.find(t => t?.destination_tank_id == currentSrcTankId);

        if (match && (!latestDate || prevDate > latestDate)) {
          latestMatch = { ...match, operation_id: prevOp?.operation_id ?? prevOp.operation_id ?? prevOp.id };
          latestDate = prevDate;
        }
      }
    }
  
    return latestMatch;
  };  
  
  const getIsHideParent = (data, operation, flowType, currentCutSrcTankId = null) => {
    const firstTransfer = operation?.transfers?.[0];

    const currentDate = new Date(operation?.completion_date);
    const currentSrcTankId = currentCutSrcTankId ? currentCutSrcTankId : firstTransfer?.source_tank_id;
    const currentDestTankId = firstTransfer?.destination_tank_id;

    return !!getMatchingTransfer(data, currentDate, currentSrcTankId, currentDestTankId, flowType);
  };

  const addEdge = ({
    edges,
    id,
    source,
    target,
    label = null,
    color,
    sourceHandle,
    targetHandle,
    isFocused = false
  }) => {
    const lineColor = isFocused && color ? getColor(color) : '#060b0f80'; // variables.$Black50
    
    edges.push({
      id,
      source,
      target,
      label,
      sourceHandle,
      targetHandle,
      animated: false,
      arrowHeadType: "arrowclosed",
      type: "smoothstep",
      style: { stroke: lineColor, strokeWidth: isFocused ? 3 : 2 },
      markerEnd: {
        type: MarkerType.Arrow,
        width: 15,
        height: 15,
        color: lineColor,
      },
    });
  }
  
  const addNode = (nodes, {
    id,
    type = "custom",
    x,
    y,
    data = {}
  }) => {
    nodes.push({
      id,
      type,
      position: { x, y },
      data,
    });
  };

  // Normalize incoming operations to legacy transfers format
  const normalizedOps = normalizeOperations(operations);

  const initialNodesAndEdges = (data) => {
    const nodes = [];
    const edges = [];
  
    const tankStateMap = new Map(); // Tracks the latest node for each tank
    
    data?.forEach((operation, i) => {
      // unify operation_id property in case new format uses `id`
      operation.operation_id = operation.operation_id ?? operation.id;

      // Handle DESTEMMING, NEW_LOT, DECANT and BOTTLING operations
      if (["DESTEMMING", "NEW_LOT", "DECANT", "RACKING", "BOTTLING", "CLEANING", "CLOSING"].includes(operation.operation_type)) {
        
        // Add a parent node for the operation
        const parentNodeId = `parent_${operation.operation_id}`;

        const isHideParent = getIsHideParent(data?.slice(0, i), operation, flowType);
        const firstTransfer = operation?.transfers?.[0];
        // if ("DESTEMMING" === operation?.operation_type) console.log('firstTransfer', firstTransfer)
        const isFocused = flowType === 'tank' && firstTransfer?.source_tank_id === Number(id);

        addNode(nodes, { // 1: Parent node
          id: parentNodeId,
          data: {
            operation_id: operation.operation_id,
            operation_type: operation.operation_type,
            completion_date: operation.completion_date,
            ...(["DESTEMMING", "NEW_LOT"].includes(operation.operation_type)
              ? { lot: { ...firstTransfer, source_batch_initial_quantity: firstTransfer.capacity ?? 1 } }
              : {
                  tank: {
                    hide: isHideParent,
                    ...firstTransfer,
                    name: firstTransfer?.source_tank_name,
                    batch_name: firstTransfer?.source_batch_name ?? firstTransfer?.batch_name,
                    batch_type: firstTransfer?.batch_type,
                    state: firstTransfer?.state,
                    quantity: firstTransfer?.src_quantity,
                    is_focused: isFocused,
                    not_focused: !isFocused,
                    type: firstTransfer?.tank_type,
                  }
                }),
          },
        });

        if (["DESTEMMING", "RACKING"].includes(operation?.operation_type) && operation?.treatments?.[0]?.quantity > 0) {
          const pomace = operation?.treatments?.[0];
          addNode(nodes, { // 2: Destination tank state
            id: `${operation.operation_id}_${pomace?.id}_drop`,
            data: {
              operation_id: operation?.operation_id,
              operation_type: operation.operation_type,
              pomace: {
                is_dest: true,
                name: pomace?.recipient || "",
                waste_type: pomace?.waste_type || null,
                quantity: (Number(pomace?.quantity) || 0),
              },
            }
          });
          // Connect parent operation node to the current tank state node
          addEdge({
            edges,
            id: `e_${parentNodeId}-${parentNodeId}_drop`,
            source: parentNodeId,
            target: `${operation.operation_id}_${pomace?.id}_drop`,
            label: `${pomace?.quantity || ''}L`,
          });
        }
  
        const currentDate = new Date(operation?.completion_date);
      
        operation?.transfers?.forEach((transfer, ti) => {

            const currentSrcTankId = transfer?.source_tank_id;

            const prevTransfer = getMatchingTransfer(data?.slice(0, i), currentDate, currentSrcTankId);
            const prevOperationId = prevTransfer?.operation_id;

            if (
              prevTransfer?.destination_tank_id &&
              transfer?.source_tank_id &&
              prevTransfer.destination_tank_id === transfer.source_tank_id
            ) {
              const sourceNodeId = `${prevOperationId}_${prevTransfer.destination_tank_id}`;
              const targetNodeId = parentNodeId;
              
              addEdge({
                edges,
                id: `e_${sourceNodeId}-${targetNodeId}`,
                source: sourceNodeId,
                target: targetNodeId,
                color: prevTransfer?.color,
                isFocused: flowType === 'tank' && transfer?.source_tank_id === Number(id),
              });           
            }
          
          const isBottling = operation?.operation_type === 'BOTTLING';
          const isClosing = operation?.operation_type === 'CLOSING';

          // Create a new node for the tank's current state
          const currentNodeId = `${operation.operation_id}_${isClosing ? transfer?.transfer_id : isBottling ? transfer?.bottling_id : transfer.destination_tank_id}`;

          const isFocused = isClosing 
            ? true 
            : flowType === 'lot'
              ? true
              // ? (batchParentIds.includes(transfer?.source_batch_id) || batchParentIds.includes(transfer?.destination_batch_id)) 
              : flowType === 'tank' && ((isBottling && transfer?.source_tank_id === Number(id)) || (!isBottling && (transfer?.destination_tank_id === Number(id))));
          
          addNode(nodes, { // 2: Destination tank state
            id: currentNodeId,
            data: {
              operation_id: operation?.operation_id,
              operation_type: operation.operation_type,
              tank: {
                is_dest: true,
                ...(isBottling && { cap_lot: transfer?.cap_lot, bottle_lot: transfer?.bottle_lot, bottle_type: transfer?.bottle_type }),
                name: isBottling ? transfer.destination_batch_name : transfer.destination_tank_name,
                batch_name: transfer.batch_name,
                batch_type: transfer?.batch_type,
                state: transfer?.state,
                quantity: (Number(transfer.quantity) || Number(transfer.arrow_quantity) || 0) - (transfer.drop || 0),
                capacity: transfer.capacity,
                color: transfer.color,
                type: transfer?.tank_type,
                is_focused: isFocused,
                not_focused: !isFocused,
                ...(isClosing &&
                {
                  close_dest: transfer?.destination_buyer_name,
                  close_reason: transfer?.destination_buyer_reason,
                }),
              },
            }
          });
          // Connect parent operation node to the current tank state node
          addEdge({
            edges,
            id: `e_${parentNodeId}-${currentNodeId}`,
            source: parentNodeId,
            target: currentNodeId,
            label: `${transfer.arrow_quantity || transfer.quantity || ''}L`,
            color: transfer?.color,
            isFocused: flowType === 'tank' && ((isBottling && transfer.source_tank_id === Number(id)) || (!isBottling && transfer.destination_tank_id === Number(id))),
          });
  
          // Check if this tank has a previous state
          const previousTankNodeId = tankStateMap.get(transfer.destination_tank_id);
          if (!isBottling && !isClosing && previousTankNodeId && previousTankNodeId !== currentNodeId) {
            addEdge({
              edges,
              id: `e_${previousTankNodeId}-${currentNodeId}`,
              source: previousTankNodeId,
              target: currentNodeId,
              color: transfer?.color,
              isFocused: flowType === 'tank' && (transfer.source_tank_id === Number(id) || transfer.destination_tank_id === Number(id)),
            });
          }
          // Update the latest state of the tank in the map
          if (transfer.destination_tank_id) tankStateMap.set(transfer.destination_tank_id, currentNodeId);
  
        });
      }
      // Handle CUT operation type
      else if (operation.operation_type === "CUT") {

        operation?.transfers?.forEach((transfer) => {
          const sourceNodeId = `${operation.operation_id}_${transfer.source_tank_id}`;

          const srcTankNodeId = tankStateMap.get(transfer.source_tank_id);

          const [prevOpId, prevDestTankId] = srcTankNodeId?.split('_')?.map(Number) || [];
          const prevOp = data?.find(op => op.operation_id === prevOpId);
          
          const isHideParent = getIsHideParent(data?.slice(0, i), operation, flowType, transfer.source_tank_id);
          addNode(nodes, { // 3: Source tank
            id: sourceNodeId,
            data: {
              operation_id: operation.operation_id,
              operation_type: operation.operation_type,
              completion_date: operation.completion_date,
              tank: {
                hide: isHideParent,
                name: transfer.source_tank_name,
                batch_name: transfer.batch_name,
                batch_type: transfer?.batch_type,
                state: transfer?.state,
                quantity: transfer.src_quantity,
                capacity: transfer.capacity,
                color: transfer.color,
                type: transfer?.source_tank_type,
              }
            }
          });
        });

        const firstTransfer = operation?.transfers?.[0];
        const destinationNodeId = `${operation.operation_id}_${firstTransfer?.destination_tank_id}`;

        addNode(nodes, { // 4: CUT destination node
          id: destinationNodeId,
          data: {
            operation_id: operation.operation_id,
            operation_type: operation.operation_type,
            completion_date: operation.completion_date,
            tank: {
              is_dest: true,
              name: firstTransfer?.destination_tank_name,
              batch_name: firstTransfer?.batch_name,
              batch_type: firstTransfer?.batch_type,
              state: firstTransfer?.state,
              quantity: firstTransfer?.quantity,
              capacity: firstTransfer?.capacity,
              color: firstTransfer?.color,
              type: firstTransfer?.tank_type,
            }
          }
        });

        if (firstTransfer?.destination_tank_id) tankStateMap.set(firstTransfer?.destination_tank_id, destinationNodeId);
      
        // Connect all source nodes to the destination node
        operation?.transfers?.forEach((transfer) => {
          const sourceNodeId = `${operation.operation_id}_${transfer.source_tank_id}`;
          addEdge({
            edges,
            id: `e_${sourceNodeId}-${destinationNodeId}`,
            source: sourceNodeId,
            target: destinationNodeId,
            label: `${transfer.quantity || ''}L`,
            color: transfer?.color,
            isFocused: flowType === 'tank' && transfer.source_tank_id === Number(id),
          });      

          const srcTankNodeId = tankStateMap.get(transfer.source_tank_id);
          const destNodeId = `${operation.operation_id}_${transfer.source_tank_id}`;

          const actualSrcTankQuantity = Number(transfer?.src_quantity) || 0;
          const [prevOpId, prevDestTankId] = srcTankNodeId?.split('_')?.map(Number) || [];
          const prevOp = data?.find(op => op.operation_id === prevOpId);
          const prevDestTankQuantity = Number(prevOp?.transfers?.find(t => t?.destination_tank_id === prevDestTankId)?.quantity) || 0;
          
          if (transfer.source_tank_id === prevDestTankId && prevDestTankQuantity === actualSrcTankQuantity) {   // if this source is dest of a previous operation

            prevOp?.transfers?.filter(t => t?.destination_tank_id === transfer.source_tank_id)?.forEach((tt) => {
              addEdge({
                edges,
                id: `e_${srcTankNodeId}-${sourceNodeId}`,
                source: srcTankNodeId,
                target: sourceNodeId,
                label: `${tt.src_arrow_quantity || ''}L`,
                color: tt?.color,
                sourceHandle: 'bottom',
                targetHandle: 'top',
              });              
            });
          } else if (prevDestTankQuantity !== actualSrcTankQuantity) {

            addEdge({
              edges,
              id: `e_${sourceNodeId}-${srcTankNodeId}`,
              source: srcTankNodeId,
              target: destNodeId,
              label: `${transfer.quantity || ''}L`,
              color: transfer?.color,
              sourceHandle: 'bottom',
              targetHandle: 'top',
            });            
          }
        });

        // Connect this destination node to the previous destination node for the same tank
        const destTankId = firstTransfer?.destination_tank_id;
        const previousDestinationNodeId = tankStateMap.get(destTankId);
        
        if (previousDestinationNodeId && previousDestinationNodeId !== destinationNodeId && flowType === 'tank' && destTankId !== Number(id)) {
          addEdge({
            edges,
            id: `e_${previousDestinationNodeId}-${destinationNodeId}`,
            source: previousDestinationNodeId,
            target: destinationNodeId,
            color: firstTransfer?.color
          });          
        }
        // Update the map with the current destination node for that tank
        if (destTankId) tankStateMap.set(destTankId, destinationNodeId);
      }      
      // Handle TREATMENT, ANALYSIS operations
      else if (["TREATMENT", "ANALYSIS", "STABILIZATION_RESIN"].includes(operation.operation_type)) {
        const parentNodeId = `parent_${operation.operation_id}`;
        if (operation.treatments?.[0]?.source_batch_color) {
          operation.treatments[0].color = getColor(operation.treatments?.[0]?.source_batch_color);
        }
        addNode(nodes, { // 5: Treatment/Analysis node
          id: parentNodeId,
          data: {
            operation_id: operation.operation_id,
            operation_type: operation.operation_type,
            completion_date: operation.completion_date,
            treatments: operation?.treatments || [],
          }
        });

        const firstTransfer = operation?.transfers?.[0];
        if (i === 0) {
          if (data[i + 1]) {
            const nextOperation = data[i + 1];
            const nextNodeId = `parent_${nextOperation.operation_id}`;
            addEdge({
              edges,
              id: `e_${parentNodeId}-${nextNodeId}`,
              source: parentNodeId,
              target: nextNodeId,
              color: firstTransfer?.color
            });            
          }
        } else {
          const prevOperation = data[i - 1];
          
          let previousTankNodeId = null;
          const isPreviousWineMovingOp = ['DESTEMMING', 'DECANT', 'RACKING', 'CUT', 'NEW_LOT', 'BOTTLING'].includes(prevOperation?.operation_type);
          if (prevOperation?.transfers?.length > 0 && isPreviousWineMovingOp) {
            const previousTransfer = prevOperation.transfers?.find((t, i) => flowType === "tank" ? t?.destination_tank_id === Number(id) : (i === (prevOperation.transfers.length - 1)));
            previousTankNodeId = isPreviousWineMovingOp ? `${prevOperation?.operation_id}_${previousTransfer?.destination_tank_id}` : `${prevOperation.operation_id}_${id}`;
          } else if (prevOperation?.treatments?.length > 0 || prevOperation?.winemakings?.length > 0 ) {
            previousTankNodeId = `parent_${prevOperation.operation_id}`;
          }
    
          if (previousTankNodeId) {
            addEdge({
              edges,
              id: `e_${previousTankNodeId}-${parentNodeId}`,
              source: previousTankNodeId,
              target: parentNodeId,
              color: firstTransfer?.color || operation?.treatments?.[0]?.color || operation?.winemakings?.[0]?.color,
              isFocused: flowType === 'tank' && (operation?.treatments?.[0]?.tank_id === Number(id) || operation?.winemakings?.[0]?.tank_id === Number(id)),
            });            
          }
        }
      }
    });
    
    const uniqueNodes = Array.from(new Map(nodes.map(n => [n.id, n])).values());
    const uniqueEdges = Array.from(new Map(edges.map(e => [e.id, e])).values());
    
    return { nodes: uniqueNodes, edges: uniqueEdges };
  };

  // Handler for node clicks
  const handleNodeClick = (event, node) => {
    const operation_id = node?.data?.operation_id;
    if (operation_id) {
      toggleShowOperationData(operation_id);
    }
  };

  const { nodes, edges } = initialNodesAndEdges(normalizedOps);
  const nodesWithLayout = applyDagreLayout(nodes, edges);

  return (
      <div style={{ height: "65vh" }}>
        <ReactFlow
          nodes={nodesWithLayout}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodeClick={handleNodeClick}
          fitView
        >
          <MiniMap nodeColor={() => "#8b0000"} />
          <Controls />
          <Background />
        </ReactFlow>
      </div>
  );
};

export default FlowChart;
